import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Form, ToggleButton, Button, ToggleButtonGroup, Offcanvas } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import DaySchedule from './DaySchedule';
import '../../assets/styles/ScheduleRuleDrawer.css';

const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // UI Mon→Sun
const DEFAULT_DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const buildAllClosedWeek = () =>
  [0, 1, 2, 3, 4, 5, 6].map((dow) => ({
    dayOfWeek: dow,
    isClosed: true,
    morningOpen: undefined,
    morningClose: undefined,
    afternoonOpen: undefined,
    afternoonClose: undefined,
  }));

/** ---------------- Timestamp without time zone helpers ----------------
 * 目标：
 * - DB: timestamp without time zone
 * - FE 传参：不带 Z，不带时区，形如 "YYYY-MM-DDTHH:mm:ss"
 * - 回填：严格 parse，不依赖 new Date(string) 的不同浏览器行为
 */
const pad2 = (n) => String(n).padStart(2, '0');

const parseTimestampNoTZ = (input) => {
  if (!input) return null;
  if (input instanceof Date) return new Date(input);

  const s = String(input).trim();
  // 兼容 "YYYY-MM-DDTHH:mm:ss" / "YYYY-MM-DD HH:mm:ss" / 只给日期
  const m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (!m) {
    // 如果后端偶尔给了 ISO（带Z），这里也尽量不炸
    const d = new Date(s);
    return isNaN(d) ? null : d;
  }

  const yyyy = Number(m[1]);
  const MM = Number(m[2]) - 1; // 0-based
  const dd = Number(m[3]);
  const hh = m[4] != null ? Number(m[4]) : 0;
  const mm = m[5] != null ? Number(m[5]) : 0;
  const ss = m[6] != null ? Number(m[6]) : 0;

  // 关键：用本地时间构造，不涉及任何时区
  const d = new Date(yyyy, MM, dd, hh, mm, ss, 0);
  return isNaN(d) ? null : d;
};

const formatTimestampNoTZ = (date, { forceYear, setToMidnight = true } = {}) => {
  if (!date) return null;
  const d = new Date(date);

  const yyyy = forceYear ?? d.getFullYear();
  const MM = d.getMonth() + 1;
  const dd = d.getDate();

  let hh = d.getHours();
  let mm = d.getMinutes();
  let ss = d.getSeconds();

  // 你这里业务是“日期区间”，通常统一用 00:00:00 最符合用户预期
  if (setToMidnight) {
    hh = 0; mm = 0; ss = 0;
  }

  return `${yyyy}-${pad2(MM)}-${pad2(dd)}T${pad2(hh)}:${pad2(mm)}:${pad2(ss)}`;
};

/** ---------- Recurring Month/Day Range Picker (UI hides year) ---------- */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const daysInMonth = (monthIndex) => new Date(2000, monthIndex + 1, 0).getDate();
const clampDay = (monthIndex, day) => Math.min(day, daysInMonth(monthIndex));

const RecurringMonthDayRange = ({ start, end, onChange }) => {
  const s = start ? new Date(start) : new Date(2000, 0, 1);
  const e = end ? new Date(end) : new Date(2000, 0, 1);

  const sMonth = s.getMonth();
  const sDay = s.getDate();
  const eMonth = e.getMonth();
  const eDay = e.getDate();

  const setStartMonth = (m) => {
    const ns = new Date(s);
    ns.setFullYear(2000);
    ns.setMonth(m);
    ns.setDate(clampDay(m, ns.getDate()));
    onChange(ns, e);
  };

  const setStartDay = (d) => {
    const ns = new Date(s);
    ns.setFullYear(2000);
    ns.setDate(clampDay(ns.getMonth(), d));
    onChange(ns, e);
  };

  const setEndMonth = (m) => {
    const ne = new Date(e);
    ne.setFullYear(2000);
    ne.setMonth(m);
    ne.setDate(clampDay(m, ne.getDate()));
    onChange(s, ne);
  };

  const setEndDay = (d) => {
    const ne = new Date(e);
    ne.setFullYear(2000);
    ne.setDate(clampDay(ne.getMonth(), d));
    onChange(s, ne);
  };

  const spansAcrossYear = sMonth > eMonth || (sMonth === eMonth && sDay > eDay);

  return (
    <div>
      <div className="range-grid">
        <div className="range-label">From</div>
        <select
          className="form-select form-select-sm"
          value={sMonth}
          onChange={(ev) => setStartMonth(Number(ev.target.value))}
        >
          {MONTHS.map((m, i) => (
            <option key={m} value={i}>
              {m}
            </option>
          ))}
        </select>
        <select
          className="form-select form-select-sm"
          value={sDay}
          onChange={(ev) => setStartDay(Number(ev.target.value))}
        >
          {Array.from({ length: daysInMonth(sMonth) }, (_, i) => i + 1).map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>

        <div className="range-label">To</div>
        <select
          className="form-select form-select-sm"
          value={eMonth}
          onChange={(ev) => setEndMonth(Number(ev.target.value))}
        >
          {MONTHS.map((m, i) => (
            <option key={m} value={i}>
              {m}
            </option>
          ))}
        </select>
        <select
          className="form-select form-select-sm"
          value={eDay}
          onChange={(ev) => setEndDay(Number(ev.target.value))}
        >
          {Array.from({ length: daysInMonth(eMonth) }, (_, i) => i + 1).map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>

      {spansAcrossYear && <div className="hint mt-2">Spans across the year (e.g., Aug → Mar).</div>}
    </div>
  );
};

/** ---------- Helpers ---------- */
const serializeDay = (d) =>
  JSON.stringify({
    isClosed: !!d.isClosed,
    morningOpen: d.morningOpen || null,
    morningClose: d.morningClose || null,
    afternoonOpen: d.afternoonOpen || null,
    afternoonClose: d.afternoonClose || null,
  });

const normalizeTemplateFromDay = (d) => ({
  isClosed: !!d?.isClosed,
  morningOpen: d?.morningOpen,
  morningClose: d?.morningClose,
  afternoonOpen: d?.afternoonOpen,
  afternoonClose: d?.afternoonClose,
});

const buildWeekFromTemplate = (template, dayNames) =>
  [0, 1, 2, 3, 4, 5, 6].map((dow) => ({
    dayOfWeek: dow,
    ...template,
    name: dayNames[dow],
  }));

/** ---------- Drawer: Closed / Modified ---------- */
const ScheduleRuleDrawer = ({
  show,
  type, // 'closed' | 'modified'
  initialData,
  onClose,
  onSubmit,
  normalizeWeek,
  denormalizeWeek,
  sortWeekForDisplay,
  dayNames = DEFAULT_DAY_NAMES,
}) => {
  const isEditing = !!initialData;

  const [isRecurring, setIsRecurring] = useState(true);

  // specific：真实年份日期（DatePicker 用）
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // recurring：固定 2000 年，仅月/日
  const [recurringStartDate, setRecurringStartDate] = useState(new Date(2000, 0, 1));
  const [recurringEndDate, setRecurringEndDate] = useState(new Date(2000, 0, 1));

  const [week, setWeek] = useState(() => normalizeWeek([], { withName: true }));

  // Modified mode
  const [mode, setMode] = useState('same'); // 'same' | 'custom'
  const [allDayTemplate, setAllDayTemplate] = useState({
    isClosed: false,
    morningOpen: '09:00',
    morningClose: '18:00',
    afternoonOpen: undefined,
    afternoonClose: undefined,
  });

  const sortForDisplay = useCallback(
    (w) => {
      if (typeof sortWeekForDisplay === 'function') return sortWeekForDisplay(w);
      return [...(w || [])].sort(
        (a, b) => DISPLAY_ORDER.indexOf(a.dayOfWeek) - DISPLAY_ORDER.indexOf(b.dayOfWeek)
      );
    },
    [sortWeekForDisplay]
  );

  const weekForDisplay = useMemo(() => sortForDisplay(week), [week, sortForDisplay]);

  // ✅ 回填：严格按 timestamp without tz 解析，避免浏览器/locale 错位
  useEffect(() => {
    const initRecurring = !!initialData?.isRecurring;
    setIsRecurring(initRecurring);

    const s = parseTimestampNoTZ(initialData?.startDate);
    const e = parseTimestampNoTZ(initialData?.endDate);

    setStartDate(s);
    setEndDate(e);

    const toRecurring = (d) => (d ? new Date(2000, d.getMonth(), d.getDate()) : new Date(2000, 0, 1));
    setRecurringStartDate(toRecurring(s));
    setRecurringEndDate(toRecurring(e));

    if (type === 'closed') {
      if (Array.isArray(initialData?.weeklySchedule) && initialData.weeklySchedule.length) {
        setWeek(normalizeWeek(initialData.weeklySchedule, { withName: true }));
      } else {
        setWeek(normalizeWeek(buildAllClosedWeek(), { withName: true }));
      }
      setMode('same');
      return;
    }

    // Modified
    if (Array.isArray(initialData?.weeklySchedule) && initialData.weeklySchedule.length) {
      const w = normalizeWeek(initialData.weeklySchedule, { withName: true });
      setWeek(w);

      const uniq = new Set(w.map(serializeDay));
      const shouldCustomize = uniq.size > 1;
      setMode(shouldCustomize ? 'custom' : 'same');

      setAllDayTemplate(normalizeTemplateFromDay(w[0]));
    } else {
      setWeek(normalizeWeek([], { withName: true }));
      setMode('same');
      setAllDayTemplate({
        isClosed: false,
        morningOpen: '09:00',
        morningClose: '18:00',
        afternoonOpen: undefined,
        afternoonClose: undefined,
      });
    }
  }, [initialData, type, normalizeWeek]);

  const onRangeChange = (dates) => {
    const [s, e] = dates;
    setStartDate(s);
    setEndDate(e);
  };

  const title =
    type === 'closed'
      ? isEditing
        ? 'Edit closure'
        : 'Add closure'
      : isEditing
        ? 'Edit temporary hours'
        : 'Add temporary hours';

  const minDate = useMemo(() => new Date(new Date().getFullYear(), 0, 1), []);
  const maxDate = useMemo(() => new Date(new Date().getFullYear() + 4, 11, 31), []);

  // ✅ 切换 recurring：仅同步月/日，不改 specific 真实日期
  useEffect(() => {
    if (isRecurring) {
      const s = startDate ? new Date(2000, startDate.getMonth(), startDate.getDate()) : new Date(2000, 0, 1);
      const e = endDate ? new Date(2000, endDate.getMonth(), endDate.getDate()) : new Date(2000, 0, 1);
      setRecurringStartDate(s);
      setRecurringEndDate(e);
    }
  }, [isRecurring]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = () => {
    const sRaw = isRecurring ? recurringStartDate : startDate;
    const eRaw = isRecurring ? recurringEndDate : endDate;

    if (!sRaw || !eRaw) return;

    let weeklySchedule;

    if (type === 'closed') {
      weeklySchedule = denormalizeWeek(normalizeWeek(buildAllClosedWeek()));
    } else {
      const w =
        mode === 'custom'
          ? week
          : normalizeWeek(buildWeekFromTemplate(allDayTemplate, dayNames), { withName: true });
      weeklySchedule = denormalizeWeek(w);
    }

    // ✅ 关键：输出 timestamp without time zone 字符串，不带 Z
    const payload = {
      ...(initialData || {}),
      isRecurring,
      startDate: formatTimestampNoTZ(sRaw, { forceYear: isRecurring ? 2000 : undefined, setToMidnight: true }),
      endDate: formatTimestampNoTZ(eRaw, { forceYear: isRecurring ? 2000 : undefined, setToMidnight: true }),
      weeklySchedule,
    };

    onSubmit(payload);
    onClose();
  };

  return (
    <Offcanvas show={show} onHide={onClose} placement="end" backdrop scroll={false} className="schedule-drawer">
      <Offcanvas.Header closeButton className="drawer-header">
        <Offcanvas.Title>{title}</Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body className="drawer-body">
        {/* Occurrence */}
        <Form.Label className="field-label d-block mb-2">When does this happen?</Form.Label>
        <ToggleButtonGroup
          type="radio"
          name="occur"
          value={isRecurring ? 'yearly' : 'specific'}
          onChange={(v) => setIsRecurring(v === 'yearly')}
          className="segmented"
        >
          <ToggleButton id="occur-specific" value="specific" variant="outline-secondary">
            On specific dates
          </ToggleButton>
          <ToggleButton id="occur-yearly" value="yearly" variant="outline-secondary">
            Repeats every year
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Date range */}
        <Form.Group className="mb-4">
          <Form.Label className="field-label d-block mb-2">Date range</Form.Label>

          {isRecurring ? (
            <RecurringMonthDayRange
              start={recurringStartDate}
              end={recurringEndDate}
              onChange={(ns, ne) => {
                setRecurringStartDate(ns);
                setRecurringEndDate(ne);
              }}
            />
          ) : (
            <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={onRangeChange}
              dateFormat="yyyy/MM/dd"
              placeholderText="Select start and end dates"
              className="form-control"
              dropdownMode="select"
              minDate={minDate}
              maxDate={maxDate}
              // ✅ 禁止手输（避免 12/25 被解析错）
              onChangeRaw={(e) => e.preventDefault()}
              // ✅ 弹层不被遮挡（推荐 portalId；没有 root-portal 就删掉 portalId 改用下面 CSS 兜底）
              portalId="root-portal"
              popperPlacement="bottom-start"
              // 兜底：给 popper 一个 class，配合 CSS 提高层级（见下方说明）
              popperClassName="datepicker-popper"
            />
          )}
        </Form.Group>

        {/* Body */}
        {type === 'closed' ? (
          <div className="p-3 border rounded bg-light">
            <div className="fw-semibold text-danger">Store is closed during this period.</div>
            <div className="text-muted small mt-1">No time settings required.</div>
          </div>
        ) : (
          <Form.Group className="mt-2">
            <Form.Label className="field-label d-block mb-2">Hours</Form.Label>

            {/* Mode switch */}
            <ToggleButtonGroup
              type="radio"
              name="mode"
              value={mode}
              onChange={(v) => setMode(v)}
              className="mode-switch"
            >
              <ToggleButton id="mode-same" value="same" variant="outline-secondary">
                Same for all days
              </ToggleButton>
              <ToggleButton id="mode-custom" value="custom" variant="outline-secondary">
                Customize by day
              </ToggleButton>
            </ToggleButtonGroup>

            {mode === 'same' ? (
              <div className="all-days-editor">
                <div className="editor-title">Apply one schedule to all days</div>

                <div className="drawer-same-days">
                  <DaySchedule
                    dayConfig={{
                      dayOfWeek: 1,
                      name: 'All days',
                      ...allDayTemplate,
                    }}
                    onChange={(updated) => {
                      setAllDayTemplate((prev) => ({
                        ...prev,
                        isClosed: !!updated.isClosed,
                        morningOpen: updated.morningOpen,
                        morningClose: updated.morningClose,
                        afternoonOpen: updated.afternoonOpen,
                        afternoonClose: updated.afternoonClose,
                      }));
                    }}
                  />
                </div>

                <div className="editor-hint">Applies to Mon–Sun.</div>
              </div>
            ) : (
              <div className="mt-3 drawer-daylist">
                <div className="text-muted small mb-2">Set hours for each day (Mon–Sun)</div>

                {weekForDisplay.map((row) => (
                  <div key={row.dayOfWeek} className="drawer-dayitem">
                    <DaySchedule
                      dayConfig={{ ...row, name: dayNames[row.dayOfWeek] || `Day ${row.dayOfWeek}` }}
                      onChange={(updated) => {
                        setWeek((prev) =>
                          prev.map((d) =>
                            d.dayOfWeek === row.dayOfWeek
                              ? { ...d, ...updated, dayOfWeek: row.dayOfWeek, name: dayNames[row.dayOfWeek] }
                              : d
                          )
                        );
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </Form.Group>
        )}
      </Offcanvas.Body>

      <div className="drawer-footer">
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={!((isRecurring ? recurringStartDate : startDate) && (isRecurring ? recurringEndDate : endDate))}>
          Save
        </Button>
      </div>
    </Offcanvas>
  );
};

export default ScheduleRuleDrawer;
